import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import useAuthStore from '../store/authStore';
import useTenantStore from '../store/tenantStore';
import api, { academicService, enrollmentService, messagingService, contentService, quizService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { IconButton, InfoTooltip, Pagination } from '../components/ui';
import { formatPrice } from '../utils/currency';
import ModuleReviews from '../components/ModuleReviews';
import EventAttendanceModal from '../components/EventAttendanceModal';
import ModuleSponsorshipBanner from '../components/ModuleSponsorshipBanner';
import CourseStatsModal from '../components/CourseStatsModal';
import PedagogicalNoteModal from '../components/PedagogicalNoteModal';
import TeacherByline from '../components/TeacherByline';
import * as XLSX from 'xlsx';
import CourseFeed from '../components/CourseFeed';
import PendingEnrollmentModal from '../components/PendingEnrollmentModal';
import { isImageAttachment, attachmentFileName } from '../utils/attachments';
import { getApiErrorMessage } from '../utils/apiErrors';

const STUDENTS_PAGE_SIZE = 20;

// One tab, active/hover states in a single place instead of ~8 near-identical
// inline buttons — a solid primary pill on the active tab (not an underline)
// so it reads consistently with the pill-style selection used across the
// sidebar and dashboards.
function TabButton({ active, onClick, icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition whitespace-nowrap text-sm ${
        active ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-surface-100'
      }`}
    >
      <span className="w-[18px] h-[18px] shrink-0">{icon}</span>
      {label}
      {badge}
    </button>
  );
}

function ModuleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewMode = new URLSearchParams(location.search).get('preview') === '1';
  const { user, isAuthenticated } = useAuthStore();
  const isFeatureEnabled = useTenantStore((state) => state.isFeatureEnabled);

  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'lessons');
  const focusedEnrollmentId = new URLSearchParams(location.search).get('enrollment');
  const [lessonProgress, setLessonProgress] = useState({});
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [publishingLessonId, setPublishingLessonId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const autoExpandedModuleRef = useRef(null);
  const [showMaterialsModal, setShowMaterialsModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [materialsTab, setMaterialsTab] = useState('materials');
  const [attachments, setAttachments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    time_limit_minutes: '',
    passing_score: '',
    randomize_questions: false,
    show_correct_answers: true,
    questions: []
  });

  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    const tabFeatures = { feed: 'community_feed', materials: 'resources', quizzes: 'quizzes', messages: 'messaging', events: 'calendar', reviews: 'ratings' };
    if (requestedTab) setActiveTab(!tabFeatures[requestedTab] || isFeatureEnabled(tabFeatures[requestedTab]) ? requestedTab : 'lessons');
  }, [location.search, isFeatureEnabled]);

  useEffect(() => {
    if (isPreviewMode && activeTab !== 'lessons') setActiveTab('lessons');
  }, [isPreviewMode, activeTab]);
  const [allModuleMaterials, setAllModuleMaterials] = useState([]);
  const [allModuleQuizzes, setAllModuleQuizzes] = useState([]);
  const [moduleQuizzesLoading, setModuleQuizzesLoading] = useState(false);
  const moduleQuizzesLoadedForRef = useRef(null);
  const moduleQuizzesRequestRef = useRef(null);
  const activeModuleIdRef = useRef(String(id));
  activeModuleIdRef.current = String(id);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [enrolledStudentCount, setEnrolledStudentCount] = useState(0);
  const [studentPage, setStudentPage] = useState(1);
  const [studentSearch, setStudentSearch] = useState('');
  const [debouncedStudentSearch, setDebouncedStudentSearch] = useState('');
  const [studentsLoading, setStudentsLoading] = useState(false);
  const studentsRequestIdRef = useRef(0);
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [pendingEnrollmentCount, setPendingEnrollmentCount] = useState(0);
  const [pendingStudentPage, setPendingStudentPage] = useState(1);
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupChat, setGroupChat] = useState(null);
  const currentUserId = user?.id;
  const [newGroupMessage, setNewGroupMessage] = useState('');
  const [groupSocket, setGroupSocket] = useState(null);
  const messagesEndRef = useRef(null);
  const groupAttachmentInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timeoutId: null });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showQuizHistoryModal, setShowQuizHistoryModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [studentProgress, setStudentProgress] = useState([]);
  const [studentQuizHistory, setStudentQuizHistory] = useState([]);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [attemptDetailQuiz, setAttemptDetailQuiz] = useState(null);
  const [loadingAttemptDetail, setLoadingAttemptDetail] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showPedagogicalNoteModal, setShowPedagogicalNoteModal] = useState(false);
  const [privateMessageText, setPrivateMessageText] = useState('');
  const [privateMessageSubject, setPrivateMessageSubject] = useState('');
  const [moduleEvents, setModuleEvents] = useState([]);
  const [attendanceEvent, setAttendanceEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [enrollForm, setEnrollForm] = useState({
    grade: '',
    subscription: '',
    student_note: '',
    attachment: null
  });
  const [enrollErrors, setEnrollErrors] = useState({});
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    meeting_link: '',
    event_date: '',
    event_time: '',
    duration_minutes: 60
  });
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'multiple_choice',
    points: 1,
    order: 0,
    image: null,
    imagePreview: null,
    options: [
      { option_text: '', is_correct: false },
      { option_text: '', is_correct: false }
    ]
  });

  // Whether current logged-in user is already enrolled (active) in this module
  const [isUserEnrolled, setIsUserEnrolled] = useState(false);
  const [currentEnrollment, setCurrentEnrollment] = useState(null);
  const [requestingCertificate, setRequestingCertificate] = useState(false);
  const [approvingCertificateId, setApprovingCertificateId] = useState(null);

  useEffect(() => {
    const isStudent = user?.roles?.some((role) => role.name?.toLowerCase() === 'student');
    if (!user || !isStudent) {
      setIsUserEnrolled(false);
      setCurrentEnrollment(null);
      return;
    }

    const checkMyEnrollment = async () => {
      try {
        const res = await enrollmentService.getModuleEnrollments(id);
        const data = res.data || res;
        const list = Array.isArray(data) ? data : data.results || [];
        const found = list.find(e => {
          const mid = e.module ? (typeof e.module === 'object' ? e.module.id : e.module) : null;
          const status = String(e.status || '').toLowerCase();
          return String(mid) === String(id) && status === 'active';
        });
        setIsUserEnrolled(!!found);
        setCurrentEnrollment(found || null);
      } catch (err) {
        console.warn('Failed to fetch my enrollments', err);
        setIsUserEnrolled(false);
        setCurrentEnrollment(null);
      }
    };

    checkMyEnrollment();
  }, [user, id]);

  const courseLessons = useMemo(
    () => module?.categories?.flatMap((category) => category.lessons || []) || [],
    [module]
  );
  const courseIsCompleted = progressLoaded && courseLessons.length > 0 && courseLessons.every(
    (lesson) => lessonProgress[lesson.id]?.is_completed
  );

  const handleRequestCertificate = async () => {
    if (!currentEnrollment) return;
    setRequestingCertificate(true);
    try {
      await enrollmentService.requestCertificate(currentEnrollment.id);
      setCurrentEnrollment((value) => ({ ...value, certificate_status: 'pending' }));
      addNotification(t('Certificate request sent to your teacher.'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to request certificate.'), 'error');
    } finally {
      setRequestingCertificate(false);
    }
  };

  const handleApproveCertificate = async (enrollmentId) => {
    setApprovingCertificateId(enrollmentId);
    try {
      await enrollmentService.approveCertificate(enrollmentId);
      addNotification(t('Certificate approved.'), 'success');
      fetchEnrolledStudents();
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Action failed'), 'error');
    } finally {
      setApprovingCertificateId(null);
    }
  };

  // Format content to remove markdown/HTML tags for preview
  const stripFormatting = (content) => {
    if (!content) return '';
    return content
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markdown
      .replace(/\*(.+?)\*/g, '$1') // Remove italic markdown
      .replace(/#+\s/g, '') // Remove heading markers
      .replace(/^-\s/gm, '• ') // Convert list markers to bullets
      .trim();
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setProgressLoaded(false);
    autoExpandedModuleRef.current = null;
    fetchModuleDetails();
    fetchLessonProgress();
  }, [id, isAuthenticated, navigate, isPreviewMode]);

  useEffect(() => {
    if (!module?.categories?.length || !progressLoaded || autoExpandedModuleRef.current === module.id) return;

    const completed = Object.values(lessonProgress)
      .filter((progress) => progress?.is_completed)
      .sort((a, b) => new Date(b.completed_at || b.last_accessed || 0) - new Date(a.completed_at || a.last_accessed || 0));
    const lastCompletedLessonId = completed[0]?.lesson;
    const targetCategory = module.categories.find((category) =>
      category.lessons?.some((lesson) => String(lesson.id) === String(lastCompletedLessonId))
    ) || module.categories[0];

    setExpandedCategories({ [targetCategory.id]: true });
    autoExpandedModuleRef.current = module.id;
  }, [module, lessonProgress, progressLoaded]);

  useEffect(() => {
    if (module && activeTab === 'materials' && isFeatureEnabled('resources')) {
      fetchModuleMaterials();
    } else if (module && activeTab === 'messages' && isFeatureEnabled('messaging')) {
      fetchGroupMessages();
    } else if (module && activeTab === 'quizzes' && isFeatureEnabled('quizzes')) {
      fetchModuleQuizzes();
    } else if (module && activeTab === 'events' && isFeatureEnabled('calendar')) {
      fetchModuleEvents();
    }
  }, [module, activeTab, isFeatureEnabled]);

  // The quizzes payload is small and is needed frequently from course details.
  // Warm it as soon as the course has loaded so opening the tab does not wait
  // for a network round-trip. fetchModuleQuizzes also deduplicates the immediate
  // tab effect above when a user clicks before this prefetch has completed.
  useEffect(() => {
    if (module?.id && !isPreviewMode && isFeatureEnabled('quizzes')) fetchModuleQuizzes();
  }, [module?.id, isPreviewMode, isFeatureEnabled]);

  useEffect(() => {
    if (module && activeTab === 'students') fetchEnrolledStudents(studentPage, debouncedStudentSearch);
  }, [module, activeTab, studentPage, debouncedStudentSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedStudentSearch(studentSearch.trim());
      setStudentPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [studentSearch]);

  useEffect(() => {
    if (module && activeTab === 'students' && checkIsTeacher()) {
      fetchPendingEnrollments(pendingStudentPage);
    }
  }, [module, activeTab, pendingStudentPage, user]);

  useEffect(() => {
    setStudentPage(1);
    setPendingStudentPage(1);
    setStudentSearch('');
    setDebouncedStudentSearch('');
    studentsRequestIdRef.current += 1;
    setAllModuleQuizzes([]);
    moduleQuizzesLoadedForRef.current = null;
    moduleQuizzesRequestRef.current = null;
  }, [id]);

  // Fetch pending enrollments as soon as module loads so teacher sees badge without clicking Students
  useEffect(() => {
    if (module && checkIsTeacher()) {
      fetchPendingEnrollments(1);
    }
  }, [module, user]);

  const [selectedEnrollmentIds, setSelectedEnrollmentIds] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [detailEnrollment, setDetailEnrollment] = useState(null);

  useEffect(() => {
    if (activeTab !== 'students' || !focusedEnrollmentId || (pendingEnrollments.length === 0 && enrolledStudents.length === 0)) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`enrollment-request-${focusedEnrollmentId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab, focusedEnrollmentId, pendingEnrollments, enrolledStudents]);

  const fetchPendingEnrollments = async (pageToLoad = pendingStudentPage) => {
    try {
      const resp = await api.get('/enrollments/enrollments/', {
        params: { module: id, status: 'pending', page: pageToLoad, page_size: STUDENTS_PAGE_SIZE },
      });
      const isPaginated = !Array.isArray(resp.data);
      const list = isPaginated ? (resp.data.results || []) : (resp.data || []);
      setPendingEnrollments(list);
      setPendingEnrollmentCount(isPaginated ? (resp.data.count || 0) : list.length);
      setSelectedEnrollmentIds([]);
      if (list.length === 0 && pageToLoad > 1) setPendingStudentPage(pageToLoad - 1);
    } catch (err) {
      console.error('Error fetching pending enrollments:', err.response?.data || err.message);
      setPendingEnrollments([]);
      setPendingEnrollmentCount(0);
    }
  };

  const handleEnrollmentDecided = () => {
    setDetailEnrollment(null);
    fetchPendingEnrollments();
    fetchEnrolledStudents();
  };

  const toggleSelectEnrollment = (enrollmentId) => {
    setSelectedEnrollmentIds((prev) =>
      prev.includes(enrollmentId) ? prev.filter((x) => x !== enrollmentId) : [...prev, enrollmentId]
    );
  };

  const handleBulkApproveEnrollments = async () => {
    if (selectedEnrollmentIds.length === 0) return;
    setBulkApproving(true);
    try {
      const res = await enrollmentService.bulkApproveEnrollments(selectedEnrollmentIds);
      addNotification(t('{{count}} enrollments approved', { count: res.data.approved }), 'success');
      fetchPendingEnrollments();
      fetchEnrolledStudents();
    } catch (err) {
      addNotification(t('Failed to approve enrollments'), 'error');
    } finally {
      setBulkApproving(false);
    }
  };


  const fetchModuleDetails = async () => {
    try {
      setLoading(true);
      const response = await academicService.getModule(id);
      const foundModule = response.data || response;
      
      if (foundModule) {
        setModule(foundModule);
      } else {
        addNotification(t('Module not found'), 'error');
        navigate('/courses');
      }
    } catch (error) {
      addNotification(t('Failed to load module details. Please try again.'), 'error');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonProgress = async () => {
    try {
      if (isPreviewMode) {
        setLessonProgress({});
        setProgressLoaded(true);
        return;
      }
      const isStudent = user?.roles?.some((role) => role.name?.toLowerCase() === 'student');
      if (!isStudent) {
        setLessonProgress({});
        setProgressLoaded(true);
        return;
      }
      // Fetch user's lesson progress
      const response = await api.get('/content/lesson-progress/', { params: { module: id } });
      const progressData = {};
      const progressList = response.data.results || response.data || [];
      progressList.forEach(progress => {
        progressData[progress.lesson] = progress;
      });
      setLessonProgress(progressData);
    } catch (error) {
      console.error('Error fetching lesson progress:', error);
    } finally {
      setProgressLoaded(true);
    }
  };

  const fetchModuleMaterials = async () => {
    try {
      if (!module || !module.categories) return;
      
      const allLessonIds = [];
      module.categories.forEach(category => {
        category.lessons?.forEach(lesson => {
          allLessonIds.push(lesson.id);
        });
      });

      const materialsPromises = allLessonIds.map(lessonId => 
        api.get(`/content/attachments/?lesson=${lessonId}`)
      );
      
      const materialsResponses = await Promise.all(materialsPromises);
      const allMaterials = [];
      
      materialsResponses.forEach((response, index) => {
        const materials = response.data.results || response.data || [];
        materials.forEach(material => {
          const lessonId = allLessonIds[index];
          const lesson = findLessonById(lessonId);
          allMaterials.push({
            ...material,
            lessonTitle: lesson?.title || 'Unknown Lesson'
          });
        });
      });
      
      setAllModuleMaterials(allMaterials);
    } catch (error) {
      console.error('Error fetching module materials:', error);
    }
  };

  const fetchModuleQuizzes = async ({ force = false } = {}) => {
    if (!module || !module.categories) return [];

    const moduleId = String(module.id);
    if (!force && moduleQuizzesLoadedForRef.current === moduleId) {
      return allModuleQuizzes;
    }
    if (!force && moduleQuizzesRequestRef.current?.moduleId === moduleId) {
      return moduleQuizzesRequestRef.current.promise;
    }

    setModuleQuizzesLoading(true);
    const request = quizService.getModuleQuizzes(module.id)
      .then((response) => {
        const quizzes = response.data.results || response.data || [];
        const allQuizzes = quizzes.map((quiz) => ({
          ...quiz,
          lessonTitle: findLessonById(quiz.lesson)?.title || 'Unknown Lesson',
        }));
        if (activeModuleIdRef.current === moduleId) {
          moduleQuizzesLoadedForRef.current = moduleId;
          setAllModuleQuizzes(allQuizzes);
        }
        return allQuizzes;
      });

    moduleQuizzesRequestRef.current = { moduleId, promise: request };
    try {
      return await request;
    } catch (error) {
      console.error('Error fetching module quizzes:', error);
      return [];
    } finally {
      if (moduleQuizzesRequestRef.current?.promise === request) {
        moduleQuizzesRequestRef.current = null;
        setModuleQuizzesLoading(false);
      }
    }
  };

  const findLessonById = (lessonId) => {
    if (!module || !module.categories) return null;
    for (const category of module.categories) {
      const lesson = category.lessons?.find(l => l.id === lessonId);
      if (lesson) return lesson;
    }
    return null;
  };

  const fetchEnrolledStudents = async (
    pageToLoad = studentPage,
    searchTerm = debouncedStudentSearch,
  ) => {
    const requestId = ++studentsRequestIdRef.current;
    setStudentsLoading(true);
    try {
      const response = await api.get('/enrollments/enrollments/', {
        params: {
          module: id,
          status: 'active',
          view: 'students',
          page: pageToLoad,
          page_size: STUDENTS_PAGE_SIZE,
          ...(searchTerm ? { search: searchTerm } : {}),
        },
      });
      const isPaginated = !Array.isArray(response.data);
      const enrollments = isPaginated ? (response.data.results || []) : (response.data || []);
      if (requestId !== studentsRequestIdRef.current) return;
      setEnrolledStudents(enrollments);
      setEnrolledStudentCount(isPaginated ? (response.data.count || 0) : enrollments.length);
      if (enrollments.length === 0 && pageToLoad > 1) setStudentPage(pageToLoad - 1);
    } catch (error) {
      if (requestId !== studentsRequestIdRef.current) return;
      console.error('Error fetching enrolled students:', error);
      console.error('Error details:', error.response?.data);
      setEnrolledStudents([]);
      setEnrolledStudentCount(0);
    } finally {
      if (requestId === studentsRequestIdRef.current) setStudentsLoading(false);
    }
  };

  const fetchGroupMessages = async () => {
    try {
      // Fetch or create group chat for this module
      const res = await messagingService.getGroupChats({ module: id });
      const chats = res.data.results || res.data || [];
      let chat = chats.find(c => String(c.module) === String(id));
      if (!chat) {
        // create a default group chat using first module grade (if available)
        const gradeId = module?.grades && module.grades.length > 0 ? module.grades[0].id : null;
        const createRes = await messagingService.createGroupChat({ module: id, grade: gradeId, name: `${module.name} - Group`, description: '' });
        chat = createRes.data;
      }
      setGroupChat(chat);

      // fetch messages for the chat
      const msgsRes = await api.get(`/messaging/group-chats/${chat.id}/messages/`);
      const messages = msgsRes.data || [];
      setGroupMessages(messages);
    } catch (error) {
      console.error('Error fetching group messages:', error);
    }
  };

  const handleSendGroupMessage = async () => {
    if (!groupChat || !newGroupMessage.trim()) return;
    try {
      // send over websocket if available, fallback to REST
      if (groupSocket && groupSocket.readyState === WebSocket.OPEN) {
        groupSocket.send(JSON.stringify({ content: newGroupMessage.trim() }));
      } else {
        const res = await messagingService.sendGroupMessage(groupChat.id, { content: newGroupMessage.trim() });
        const msg = res.data;
        setGroupMessages(prev => (msg?.id != null && prev.some(m => m.id === msg.id)) ? prev : [...prev, msg]);
      }
      setNewGroupMessage('');
    } catch (err) {
      console.error('Failed to send group message', err);
      addNotification(t('Failed to send message'), 'error');
    }
  };

  const handleGroupAttachmentChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !groupChat) return;
    if (file.size > 25 * 1024 * 1024) {
      addNotification(t('Attachment must be smaller than 25MB'), 'error');
      return;
    }
    const formData = new FormData();
    formData.append('attachment', file);
    try {
      const res = await messagingService.sendGroupMessage(groupChat.id, formData);
      const msg = res.data;
      setGroupMessages(prev => (msg?.id != null && prev.some(m => m.id === msg.id)) ? prev : [...prev, msg]);
    } catch (err) {
      addNotification(t('Failed to send attachment'), 'error');
    }
  };

  // Open websocket to group chat for real-time messages
  useEffect(() => {
    if (!groupChat) return;
    const token = localStorage.getItem('accessToken');
    const host = window.location.hostname;
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // If running on localhost, backend is assumed to run on port 8000 (Daphne)
    const backendPort = (host === 'localhost' || host === '127.0.0.1') ? ':8000' : '';
    const url = `${protocol}://${host}${backendPort}/ws/messaging/group/${groupChat.id}/?token=${token}`;

    let ws;
    const createWs = () => {
      ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('Group WS connected', url);
        reconnectRef.current.attempts = 0;
        if (reconnectRef.current.timeoutId) {
          clearTimeout(reconnectRef.current.timeoutId);
          reconnectRef.current.timeoutId = null;
        }
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'message') {
            const msg = data.payload;
            setGroupMessages(prev => {
              const next = [...prev, msg];
              return next;
            });
          } else if (data.type === 'read') {
            const payload = data.payload || {};
            const messageIds = payload.message_ids || [];
            setGroupMessages(prev => prev.map(m => {
              if (messageIds.includes(m.id) && m.sender && String(m.sender.id) === String(currentUserId)) {
                return { ...m, is_read: true };
              }
              return m;
            }));
          }
          // scroll to bottom after DOM updates
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        } catch (e) { console.error('WS parse error', e); }
      };

      ws.onclose = () => {
        console.log('Group WS closed, scheduling reconnect');
        // attempt reconnect with exponential backoff (cap attempts)
        reconnectRef.current.attempts += 1;
        const backoff = Math.min(30000, 1000 * 2 ** (reconnectRef.current.attempts - 1));
        reconnectRef.current.timeoutId = setTimeout(() => createWs(), backoff);
      };

      ws.onerror = (e) => console.error('Group WS error', e);

      setGroupSocket(ws);
    };

    createWs();

    return () => {
      try { ws.close(); } catch (e) {}
      setGroupSocket(null);
      if (reconnectRef.current.timeoutId) clearTimeout(reconnectRef.current.timeoutId);
      reconnectRef.current.attempts = 0;
    };
  }, [groupChat]);

  useEffect(() => {
    // scroll when initially loading messages
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
  }, [groupMessages.length]);

  const fetchStudentProgress = async (studentId) => {
    try {
      const response = await api.get(`/content/lesson-progress/?student=${studentId}`);
      const allProgress = response.data.results || response.data || [];
      
      // Filter progress for lessons in this module
      const moduleLessonIds = [];
      module.categories?.forEach(cat => {
        cat.lessons?.forEach(lesson => moduleLessonIds.push(lesson.id));
      });
      
      const filteredProgress = allProgress.filter(p => moduleLessonIds.includes(p.lesson));
      setStudentProgress(filteredProgress);
    } catch (error) {
      console.error('Error fetching student progress:', error);
    }
  };

  const fetchStudentQuizHistory = async (studentId) => {
    try {
      const response = await api.get('/quizzes/attempts/', {
        params: { student: studentId, module: module.id, page_size: 100 },
      });
      const allAttempts = response.data.results || response.data || [];
      setStudentQuizHistory(allAttempts);
    } catch (error) {
      console.error('Error fetching student quiz history:', error);
    }
  };

  // Attempt history is intentionally compact. Fetch the selected attempt's
  // per-question responses only when the detail modal is opened.
  const openAttemptDetail = async (attempt) => {
    setAttemptDetail(attempt);
    setAttemptDetailQuiz(null);
    setLoadingAttemptDetail(true);
    try {
      const [attemptRes, quizRes] = await Promise.all([
        quizService.getAttempt(attempt.id),
        quizService.getQuiz(attempt.quiz),
      ]);
      setAttemptDetail(attemptRes.data || attemptRes);
      setAttemptDetailQuiz(quizRes.data || quizRes);
    } catch (error) {
      addNotification(t('Failed to load attempt details'), 'error');
    } finally {
      setLoadingAttemptDetail(false);
    }
  };

  const closeAttemptDetail = () => {
    setAttemptDetail(null);
    setAttemptDetailQuiz(null);
  };

  const fetchModuleEvents = async () => {
    try {
      const response = await enrollmentService.getEvents({ module: id });
      const raw = response.data.results || response.data || [];
      const events = raw.map(ev => {
        const start = ev.start_datetime || ev.start || ev.event_datetime;
        const end = ev.end_datetime || ev.end || null;
        let duration = null;
        try {
          if (start && end) {
            duration = Math.round((new Date(end) - new Date(start)) / 60000);
          } else if (ev.duration_minutes) {
            duration = ev.duration_minutes;
          }
        } catch (e) {
          duration = ev.duration_minutes || null;
        }
        return {
          ...ev,
          event_datetime: start,
          duration_minutes: duration,
          meeting_link: ev.url || ev.meeting_link || '',
          location: ev.location || ''
        };
      });
      setModuleEvents(events);
    } catch (error) {
      console.error('Error fetching module events:', error);
    }
  };

  const handleEventFormChange = (e) => {
    const { name, value } = e.target;
    setEventForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    if (!eventForm.title || !eventForm.event_date || !eventForm.event_time) {
      addNotification(t('Please fill in all required fields'), 'error');
      return;
    }

    try {
      const eventDateTime = `${eventForm.event_date}T${eventForm.event_time}:00`;
      const start = new Date(eventDateTime);
      const end = new Date(start.getTime() + (parseInt(eventForm.duration_minutes || 60) * 60000));

      await enrollmentService.createEvent({
        module: parseInt(id),
        title: eventForm.title,
        description: eventForm.description,
        url: eventForm.meeting_link || '',
        start_datetime: start.toISOString(),
        end_datetime: end.toISOString(),
        location: eventForm.location || ''
      });

      addNotification(t('Event created successfully! All enrolled students have been notified.'), 'success');
      setShowEventModal(false);
      setEventForm({
        title: '',
        description: '',
        meeting_link: '',
        event_date: '',
        event_time: '',
        duration_minutes: 60
      });
      fetchModuleEvents();
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to create event'), 'error');
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCompleteLesson = async (lessonId) => {
    try {
      const progress = lessonProgress[lessonId];
      const completedAt = new Date().toISOString();
      let response;
      if (progress) {
        // Update existing progress
        response = await api.patch(`/content/lesson-progress/${progress.id}/`, {
          is_completed: true,
          completion_percentage: 100,
          completed_at: completedAt,
        });
      } else {
        // Create new progress entry
        response = await api.post('/content/lesson-progress/', {
          lesson: lessonId,
          is_completed: true,
          completion_percentage: 100,
          completed_at: completedAt,
        });
      }
      const savedProgress = response.data || response;
      setLessonProgress((current) => ({
        ...current,
        [lessonId]: {
          ...current[lessonId],
          ...savedProgress,
          lesson: lessonId,
          is_completed: true,
          completion_percentage: 100,
          completed_at: savedProgress.completed_at || completedAt,
        },
      }));
    } catch (error) {
      console.error('Error completing lesson:', error);
    }
  };

  const openMaterialsModal = async (lesson) => {
    setSelectedLesson(lesson);
    setShowMaterialsModal(true);
    setMaterialsTab(isFeatureEnabled('resources') ? 'materials' : 'quiz');
    await fetchLessonMaterials(lesson.id);
  };

  const fetchLessonMaterials = async (lessonId) => {
    try {
      const [attachmentsRes, quizzesRes] = await Promise.all([
        isFeatureEnabled('resources') ? api.get(`/content/attachments/?lesson=${lessonId}`) : Promise.resolve({ data: [] }),
        isFeatureEnabled('quizzes') ? api.get(`/quizzes/quizzes/?lesson=${lessonId}`) : Promise.resolve({ data: [] }),
      ]);
      setAttachments(attachmentsRes.data.results || attachmentsRes.data || []);
      setQuizzes(quizzesRes.data.results || quizzesRes.data || []);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const handleMaterialFormChange = (e) => {
    const { name, value } = e.target;
    setMaterialForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setMaterialForm(prev => ({ ...prev, file: e.target.files[0] }));
  };

  const handleUploadMaterial = async (e) => {
    e.preventDefault();
    if (!materialForm.file || !materialForm.title) {
      addNotification(t('Please provide title and file'), 'error');
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('lesson', selectedLesson.id);
    formData.append('title', materialForm.title);
    formData.append('description', materialForm.description);
    formData.append('file', materialForm.file);
    formData.append('attachment_type', 1); // Default type

    try {
      await api.post('/content/attachments/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      addNotification(t('Material uploaded successfully!'), 'success');
      setMaterialForm({ title: '', description: '', file: null });
      await fetchLessonMaterials(selectedLesson.id);
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to upload material'), 'error');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleQuizFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuizForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDeleteMaterial = async (attachmentId) => {
    if (!(await confirm(t('Are you sure you want to delete this material?')))) return;
    try {
      await api.delete(`/content/attachments/${attachmentId}/`);
      addNotification(t('Material deleted successfully!'), 'success');
      // Refresh materials list
      await fetchLessonMaterials(selectedLesson.id);
    } catch (error) {
      addNotification(t('Failed to delete material'), 'error');
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!(await confirm(t('Are you sure you want to delete this quiz? This will also delete all student attempts.')))) return;
    try {
      await quizService.deleteQuiz(quizId);
      addNotification(t('Quiz deleted successfully!'), 'success');
      if (selectedLesson) await fetchLessonMaterials(selectedLesson.id);
      await fetchModuleQuizzes({ force: true });
    } catch (error) {
      addNotification(t('Failed to delete quiz'), 'error');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!(await confirm(t('Delete {{name}}? This cannot be undone.', { name: category.name })))) return;
    try {
      await academicService.deleteCategory(category.id);
      setModule((current) => current ? {
        ...current,
        categories: (current.categories || []).filter((item) => item.id !== category.id),
      } : current);
      addNotification(t('Category deleted successfully!'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to delete category'), 'error');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!(await confirm(t('Are you sure you want to delete this lesson? This will also delete its materials and quizzes.')))) return;
    try {
      await contentService.deleteLesson(lessonId);
      addNotification(t('Lesson deleted successfully!'), 'success');
      await fetchModuleDetails();
    } catch (error) {
      addNotification(t('Failed to delete lesson'), 'error');
    }
  };

  const handlePublishLesson = async (lessonId) => {
    setPublishingLessonId(lessonId);
    try {
      const response = await contentService.publishLesson(lessonId);
      const publishedLesson = response.data || response;
      setModule((current) => current ? ({
        ...current,
        categories: current.categories?.map((category) => ({
          ...category,
          lessons: category.lessons?.map((lesson) =>
            lesson.id === lessonId ? { ...lesson, ...publishedLesson, is_published: true } : lesson
          ),
        })),
        lessons: current.lessons?.map((lesson) =>
          lesson.id === lessonId ? { ...lesson, ...publishedLesson, is_published: true } : lesson
        ),
      }) : current);
      addNotification(t('Lesson published successfully'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to publish lesson'), 'error');
    } finally {
      setPublishingLessonId(null);
    }
  };

  const handleQuestionFormChange = (e) => {
    const { name, value } = e.target;
    setQuestionForm(prev => ({ ...prev, [name]: value }));
  };

  const handleOptionChange = (index, field, value) => {
    setQuestionForm(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const addOption = () => {
    setQuestionForm(prev => ({
      ...prev,
      options: [...prev.options, { option_text: '', is_correct: false }]
    }));
  };

  const removeOption = (index) => {
    setQuestionForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const MAX_QUESTION_IMAGE_SIZE = 5 * 1024 * 1024;

  const handleQuestionImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addNotification(t('Please select an image file'), 'error');
      return;
    }
    if (file.size > MAX_QUESTION_IMAGE_SIZE) {
      addNotification(t('Image must be smaller than 5MB'), 'error');
      return;
    }
    setQuestionForm(prev => {
      if (prev.imagePreview) URL.revokeObjectURL(prev.imagePreview);
      return { ...prev, image: file, imagePreview: URL.createObjectURL(file) };
    });
  };

  const removeQuestionImage = () => {
    setQuestionForm(prev => {
      if (prev.imagePreview) URL.revokeObjectURL(prev.imagePreview);
      return { ...prev, image: null, imagePreview: null };
    });
  };

  const EXCEL_TEMPLATE_ROWS = [
    {
      Question: 'What is the capital of France?',
      'Option 1': 'London',
      'Option 2': 'Paris',
      'Option 3': 'Berlin',
      'Option 4': 'Madrid',
      'Correct Answer': '2',
      Points: 1,
      Explanation: 'Paris has been the capital of France since the 12th century.',
    },
    {
      Question: 'Which of these are prime numbers? (select all that apply)',
      'Option 1': '2',
      'Option 2': '4',
      'Option 3': '7',
      'Option 4': '9',
      'Correct Answer': '1,3',
      Points: 2,
      Explanation: '',
    },
  ];

  const downloadExcelTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(EXCEL_TEMPLATE_ROWS, {
      header: ['Question', 'Option 1', 'Option 2', 'Option 3', 'Option 4', 'Option 5', 'Option 6', 'Correct Answer', 'Points', 'Explanation'],
    });
    ws['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 8 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Questions');
    XLSX.writeFile(wb, 'quiz_questions_template.xlsx');
  };

  const handleExcelImport = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const imported = [];
        const rowErrors = [];
        const optionKeys = ['Option 1', 'Option 2', 'Option 3', 'Option 4', 'Option 5', 'Option 6'];

        rows.forEach((row, idx) => {
          const rowNum = idx + 2; // header is row 1
          const questionText = String(row['Question'] || '').trim();
          if (!questionText) {
            rowErrors.push(t('Row {{row}}: missing question text', { row: rowNum }));
            return;
          }

          const options = optionKeys
            .map((key) => String(row[key] || '').trim())
            .filter((text) => text.length > 0)
            .map((text) => ({ option_text: text, is_correct: false }));

          if (options.length < 2) {
            rowErrors.push(t('Row {{row}}: needs at least 2 options', { row: rowNum }));
            return;
          }

          const correctIndexes = String(row['Correct Answer'] || '')
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => Number.isInteger(n) && n >= 1 && n <= options.length);

          if (correctIndexes.length === 0) {
            rowErrors.push(t('Row {{row}}: "Correct Answer" must reference at least one option number (1-{{count}})', { row: rowNum, count: options.length }));
            return;
          }

          correctIndexes.forEach((n) => { options[n - 1].is_correct = true; });

          imported.push({
            question_text: questionText,
            question_type: 'multiple_choice',
            points: parseInt(row['Points'], 10) || 1,
            order: quizForm.questions.length + imported.length + 1,
            image: null,
            imagePreview: null,
            explanation: String(row['Explanation'] || '').trim(),
            allow_multiple_answers: correctIndexes.length > 1,
            options,
          });
        });

        if (imported.length > 0) {
          setQuizForm((prev) => ({ ...prev, questions: [...prev.questions, ...imported] }));
        }

        if (rowErrors.length > 0) {
          addNotification(
            t('Imported {{count}} question(s); {{errCount}} row(s) skipped — {{errors}}', {
              count: imported.length, errCount: rowErrors.length, errors: rowErrors.slice(0, 3).join('; '),
            }),
            imported.length > 0 ? 'success' : 'error'
          );
        } else {
          addNotification(t('Imported {{count}} question(s) from Excel', { count: imported.length }), 'success');
        }
      } catch (err) {
        addNotification(t('Failed to read the Excel file. Make sure it matches the template format.'), 'error');
      }
    };
    reader.readAsBinaryString(file);
  };

  const addQuestionToQuiz = () => {
    if (!questionForm.question_text || questionForm.options.length < 2) {
      addNotification(t('Please provide question text and at least 2 options'), 'error');
      return;
    }
    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, { ...questionForm }]
    }));
    addNotification(t('Question added to quiz'), 'success');
    setQuestionForm({
      question_text: '',
      question_type: 'multiple_choice',
      points: 1,
      order: quizForm.questions.length + 1,
      image: null,
      imagePreview: null,
      options: [
        { option_text: '', is_correct: false },
        { option_text: '', is_correct: false }
      ]
    });
  };
  const handleCreateQuiz = async (e) => {
    e.preventDefault();

    // The "Add Question" box below is a separate staging area — filling it in
    // doesn't add it to the quiz until "+ Add Question to Quiz" is clicked.
    // If the teacher instead goes straight for "Create Quiz", auto-include
    // whatever they typed there instead of silently dropping it.
    let questions = quizForm.questions;
    const draftHasText = questionForm.question_text.trim().length > 0;
    const draftValidOptions = questionForm.options.filter(o => o.option_text.trim().length > 0).length;
    if (draftHasText && draftValidOptions >= 2) {
      questions = [...questions, { ...questionForm }];
    }

    if (!quizForm.title || questions.length === 0) {
      addNotification(t('Please provide quiz title and at least one question'), 'error');
      return;
    }

    try {
      const quizData = {
        lesson: selectedLesson.id,
        title: quizForm.title,
        description: quizForm.description,
        time_limit_minutes: quizForm.time_limit_minutes ? parseInt(quizForm.time_limit_minutes) : null,
        passing_score: quizForm.passing_score ? parseInt(quizForm.passing_score) : 70,
        randomize_questions: quizForm.randomize_questions,
        show_correct_answers: quizForm.show_correct_answers,
        is_published: true
      };

      const quizResponse = await api.post('/quizzes/quizzes/', quizData);

      const quizId = quizResponse.data.id;

      // Create questions
      for (const question of questions) {
        let questionResponse;
        if (question.image) {
          const fd = new FormData();
          fd.append('quiz', quizId);
          fd.append('text', question.question_text);
          fd.append('points', question.points);
          fd.append('order', question.order);
          fd.append('allow_multiple_answers', question.allow_multiple_answers || false);
          if (question.explanation) fd.append('explanation', question.explanation);
          fd.append('image', question.image);
          questionResponse = await api.post('/quizzes/questions/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          const questionData = {
            quiz: quizId,
            text: question.question_text,
            points: question.points,
            order: question.order,
            allow_multiple_answers: question.allow_multiple_answers || false,
            explanation: question.explanation || '',
          };
          questionResponse = await api.post('/quizzes/questions/', questionData);
        }

        const questionId = questionResponse.data.id;

        // Create answers
        for (const option of question.options) {
          await api.post('/quizzes/answers/', {
            question: questionId,
            text: option.option_text,
            is_correct: option.is_correct,
            order: 0
          });
        }
      }

      addNotification(t('Quiz created successfully!'), 'success');
      setQuizForm({
        title: '',
        description: '',
        time_limit_minutes: '',
        passing_score: '',
        randomize_questions: false,
        show_correct_answers: true,
        questions: []
      });
      setQuestionForm({
        question_text: '',
        question_type: 'multiple_choice',
        points: 1,
        order: 0,
        image: null,
        imagePreview: null,
        options: [
          { option_text: '', is_correct: false },
          { option_text: '', is_correct: false }
        ]
      });
      await fetchLessonMaterials(selectedLesson.id);
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to create quiz'), 'error');
    }
  };

  function checkIsTeacher() {
    const hasTeacherRole = user?.roles?.some(role => role.name?.toLowerCase() === 'teacher');
    if (!hasTeacherRole) return false;
    if (!module) return true;
    const creatorId = typeof module.created_by === 'object' ? module.created_by?.id : module.created_by;
    const assigned = module.teachers?.some((teacher) =>
      String(typeof teacher === 'object' ? teacher.id : teacher) === String(user.id)
    );
    return String(creatorId) === String(user.id) || !!assigned;
  }

  const orderedLessons = useMemo(
    () => module?.categories?.flatMap((category) => category.lessons || []) || [],
    [module],
  );

  function isLessonLocked(lesson) {
    if (isPreviewMode) return !lesson.is_free;
    if (checkIsTeacher() || !module?.sequential_progress || lesson.is_free) return false;
    if (!progressLoaded) return !!lesson.is_locked;
    const lessonIndex = orderedLessons.findIndex((item) => item.id === lesson.id);
    if (lessonIndex <= 0) return false;
    const previousLesson = orderedLessons[lessonIndex - 1];
    return !lessonProgress[previousLesson.id]?.is_completed;
  }

  const fetchSubscriptions = async () => {
    try {
      const res = await api.get('/enrollments/subscriptions/', {
        params: { module: module.id, is_active: true },
      });
      const data = res.data.results || res.data || [];
      const list = Array.isArray(data) ? data : data.results || [];
      setSubscriptions(list);
      return list;
    } catch (err) {
      console.error('Failed to load subscriptions', err);
      return [];
    }
  };

  const openEnrollModal = async () => {
    const subs = await fetchSubscriptions();
    // default grade to first available
    setEnrollForm(prev => ({
      ...prev,
      grade: module.grades && module.grades.length > 0 ? module.grades[0].id : '',
      subscription: subs && subs.length > 0 ? subs[0].id : ''
    }));
    setShowEnrollModal(true);
  };

  const handleEnrollChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'attachment') {
      const file = files[0];
      setEnrollForm(prev => ({ ...prev, attachment: file }));
      // validate attachment immediately
      const err = validateAttachment(file);
      setEnrollErrors(prev => ({ ...prev, attachment: err }));
    } else {
      setEnrollForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateAttachment = (file) => {
    if (!file) return '';
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      return t('Invalid file type. Allowed: pdf, jpg, jpeg, png.');
    }
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return t('File too large. Max size is 5MB.');
    }
    return '';
  };

  const submitEnrollment = async (e) => {
    e.preventDefault();
    // Validate before submit
    const attachErr = validateAttachment(enrollForm.attachment);
    if (attachErr) {
      setEnrollErrors(prev => ({ ...prev, attachment: attachErr }));
      addNotification(attachErr, 'error');
      return;
    }

    try {
      const fd = new FormData();
      fd.append('module', id);
      if (enrollForm.grade) fd.append('grade', enrollForm.grade);
      if (enrollForm.subscription) fd.append('subscription', enrollForm.subscription);
      if (enrollForm.student_note) fd.append('student_note', enrollForm.student_note);
      if (enrollForm.attachment) fd.append('attachment', enrollForm.attachment);

      await api.post('/enrollments/enrollments/', fd);
      addNotification(t('Enrollment request sent. Awaiting approval.'), 'success');
      setShowEnrollModal(false);
      fetchEnrolledStudents();
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to submit enrollment'), 'error');
    }
  };

  if (loading || !module) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('Loading...')} />
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={module.name} subtitle={module.code} />
        
        <main className="flex-1 overflow-y-auto p-6">
          {/* Module Header — the app's brand gradient + dot-grid when there's
              no photo, matching the dashboard hero banners; the photo case
              keeps a darkened backdrop so the same white text stays legible. */}
          <div
            className={`relative overflow-hidden rounded-2xl p-5 sm:p-8 text-white mb-6 ${module.thumbnail ? 'bg-cover bg-center' : 'bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500'}`}
            style={module.thumbnail ? { backgroundImage: `url(${module.thumbnail}${module.updated_at ? `?v=${new Date(module.updated_at).getTime()}` : `?v=${Date.now()}`})` } : {}}
          >
            {module.thumbnail ? (
              <div className="absolute inset-0 bg-black/40 pointer-events-none" />
            ) : (
              <div
                className="absolute inset-0 opacity-[0.15] pointer-events-none"
                style={{
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                }}
              />
            )}
            <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-3xl font-display font-bold mb-2 break-words text-balance">{module.name}</h1>
                <TeacherByline teachers={module.teachers} className="mb-2" dark />

                <p className="text-primary-100 mb-4 text-sm sm:text-base">{module.description}</p>
                {module.enrollment_directives && (
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 mb-4 border border-white/10">
                    <h4 className="text-sm font-semibold text-white mb-1">{t('Enrollment Instructions')}</h4>
                    <p className="text-primary-100 text-sm">{module.enrollment_directives}</p>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-xs sm:text-sm font-medium">{module.code}</span>
                  <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-xs sm:text-sm font-medium">{module.price && Number(module.price) > 0 ? formatPrice(module.price, module.currency) : t('Free')}</span>
                  {module.grades && module.grades.length > 0 && (
                    <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                      {module.grades.map(g => g.name).join(', ')}
                    </span>
                  )}
                  <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                    {t('{{count}} Categories', { count: module.categories?.length || 0 })}
                  </span>
                  {checkIsTeacher() && (
                    <ModuleSponsorshipBanner module={module} onRefresh={fetchModuleDetails} />
                  )}
                </div>
                {(!checkIsTeacher() && !isUserEnrolled) && (
                  <button
                    onClick={openEnrollModal}
                    className="mt-4 px-5 py-2 bg-white text-primary-700 font-semibold rounded-xl hover:bg-primary-50 transition shadow-sm"
                  >
                    {t('Enroll')}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0 self-start">
                {checkIsTeacher() && (
                  <button
                    onClick={() => navigate(`/modules/${id}/edit`)}
                    className="bg-white/15 backdrop-blur-sm hover:bg-white/25 p-2 rounded-xl transition"
                    title={t('Edit Module')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => navigate('/courses')}
                  className="bg-white/15 backdrop-blur-sm hover:bg-white/25 px-4 py-2 rounded-xl transition font-medium"
                >
                  ← {t('Back')}
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-card mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center border-b border-surface-100">
            <div className="flex overflow-x-auto flex-1 min-w-0 gap-1 p-2">
              <TabButton
                active={activeTab === 'lessons'}
                onClick={() => setActiveTab('lessons')}
                label={t('Lessons')}
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                }
              />
              {!isPreviewMode && (
              <>
              {isFeatureEnabled('community_feed') && <TabButton
                active={activeTab === 'feed'}
                onClick={() => setActiveTab('feed')}
                label={t('Feed')}
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2zM7 8h10M7 12h10M7 16h6" />
                  </svg>
                }
              />}
              {isFeatureEnabled('resources') && <TabButton
                active={activeTab === 'materials'}
                onClick={() => setActiveTab('materials')}
                label={t('Materials')}
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                }
              />}
              {isFeatureEnabled('quizzes') && <TabButton
                active={activeTab === 'quizzes'}
                onClick={() => setActiveTab('quizzes')}
                label={t('Quizzes')}
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                }
              />}
              {checkIsTeacher() && (
                <TabButton
                  active={activeTab === 'students'}
                  onClick={() => setActiveTab('students')}
                  label={t('Students')}
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  }
                  badge={pendingEnrollmentCount > 0 && (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full min-w-[18px]">{pendingEnrollmentCount}</span>
                  )}
                />
              )}
              {isFeatureEnabled('messaging') && <TabButton
                active={activeTab === 'messages'}
                onClick={() => setActiveTab('messages')}
                label={t('Messages')}
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                }
              />}
              {checkIsTeacher() && isFeatureEnabled('calendar') && (
                <TabButton
                  active={activeTab === 'events'}
                  onClick={() => setActiveTab('events')}
                  label={t('Events')}
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  }
                />
              )}
              {isFeatureEnabled('ratings') && <TabButton
                active={activeTab === 'reviews'}
                onClick={() => setActiveTab('reviews')}
                label={t('Reviews')}
                icon={
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.5l2.9 6.26 6.9.79-5.13 4.78 1.4 6.83L12 17.6l-6.07 3.56 1.4-6.83L2.2 9.55l6.9-.79L12 2.5z" />
                  </svg>
                }
                badge={module.average_rating != null && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === 'reviews' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`}>
                    {Number(module.average_rating).toFixed(1)}
                  </span>
                )}
              />}
              </>
              )}
            </div>
            {checkIsTeacher() && !isPreviewMode && (
              <div className="flex gap-2 px-4 py-3 sm:px-6 border-t sm:border-t-0 sm:border-l border-surface-100 shrink-0">
                <IconButton
                  color="green"
                  label={t('Add Category')}
                  onClick={() => navigate(`/modules/${id}/categories/create`)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v4m-2-2h4" />
                    </svg>
                  }
                />
                <IconButton
                  color="primary"
                  label={t('Add Lesson')}
                  onClick={() => navigate(`/modules/${id}/lessons/create`)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  }
                />
                <IconButton
                  color="purple"
                  label={t('Course Statistics')}
                  onClick={() => setShowStatsModal(true)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2" />
                    </svg>
                  }
                />
                <IconButton
                  color="yellow"
                  label={t('Generate Teaching Note')}
                  onClick={() => setShowPedagogicalNoteModal(true)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  }
                />
              </div>
            )}
            </div>
          </div>

          {/* Content */}
          {activeTab === 'feed' && isFeatureEnabled('community_feed') && (
            <CourseFeed
              moduleId={module.id}
              isTeacher={checkIsTeacher()}
              isAdmin={user?.roles?.some((r) => r.name?.toLowerCase() === 'administrator')}
              currentUser={user}
            />
          )}
          {activeTab === 'lessons' && (
            <div className="space-y-6">
              {isPreviewMode && (
                <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <h2 className="font-display text-lg font-bold text-primary-900">{t('Course preview')}</h2>
                    <p className="mt-1 text-sm text-primary-700">
                      {t('Preview mode does not save lesson progress or quiz attempts. Only free lessons are available.')}
                    </p>
                  </div>
                  {!isUserEnrolled && (
                    <button onClick={openEnrollModal} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition shrink-0">
                      {t('Enroll for full access')}
                    </button>
                  )}
                </div>
              )}
              {courseIsCompleted && currentEnrollment && (
                <div className="rounded-2xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-6 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-600 text-white flex items-center justify-center shrink-0">
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-green-900">{t('Congratulations!')}</h2>
                      <p className="text-green-700">{t('You completed this course.')}</p>
                      {isFeatureEnabled('certificates') && currentEnrollment.certificate_status === 'pending' && (
                        <p className="text-sm text-green-700 mt-1">{t('Your certificate request is awaiting teacher approval.')}</p>
                      )}
                    </div>
                    {isFeatureEnabled('certificates') && (currentEnrollment.has_certificate ? (
                      <button onClick={() => navigate(`/enrollments?enrollment=${currentEnrollment.id}`)} className="px-4 py-2.5 bg-white border border-green-300 text-green-800 rounded-xl font-semibold hover:bg-green-100 transition">
                        {t('Download Certificate')}
                      </button>
                    ) : currentEnrollment.certificate_status === 'pending' ? (
                      <span className="px-4 py-2.5 bg-amber-100 text-amber-800 rounded-xl font-semibold">{t('Approval Pending')}</span>
                    ) : (
                      <button disabled={requestingCertificate} onClick={handleRequestCertificate} className="px-4 py-2.5 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition disabled:opacity-60">
                        {requestingCertificate ? t('Sending...') : t('Request Certificate')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Intro video if present - show before categories */}
              {module.intro_video && (
                <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                  <div className="p-6">
                    {module.intro_video.includes('youtube') || module.intro_video.includes('youtu.be') ? (
                      <div className="w-full aspect-w-16 aspect-h-9">
                        <iframe
                          title={t('Intro video')}
                          src={module.intro_video.includes('embed') ? module.intro_video : module.intro_video.includes('watch') ? module.intro_video.replace('watch?v=', 'embed/') : module.intro_video.replace('youtu.be/', 'www.youtube.com/embed/')}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-64 rounded-md"
                        />
                      </div>
                    ) : (
                      <video src={module.intro_video} controls className="w-full rounded-md" />
                    )}
                    {module.description && (
                      <p className="text-gray-600 text-sm mt-3">{module.description}</p>
                    )}
                  </div>
                </div>
              )}
              {module.categories && module.categories.length > 0 ? (
                module.categories.map((category) => {
                  const isExpanded = expandedCategories[category.id];
                  const lessonCount = category.lessons?.length || 0;
                  const completedCount = isPreviewMode
                    ? 0
                    : category.lessons?.filter(lesson => lessonProgress[lesson.id]?.is_completed).length || 0;
                  const freeCount = category.lessons?.filter((lesson) => lesson.is_free).length || 0;
                  
                  return (
                    <div key={category.id} className="bg-white rounded-2xl shadow-card overflow-hidden">
                      <div
                        className="px-5 py-4 cursor-pointer hover:bg-surface-50 transition flex items-center justify-between gap-4"
                        onClick={() => toggleCategory(category.id)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="shrink-0 w-9 h-9 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
                            <svg
                              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-45' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </span>
                          <div className="min-w-0">
                            <h2 className="text-base font-display font-bold text-gray-800 truncate">{category.name}</h2>
                            {category.description && (
                              <p className="text-gray-500 text-sm truncate">{category.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-gray-600 text-xs font-medium tabular-nums">
                            {isPreviewMode
                              ? t('{{free}} free / {{total}} lessons', { free: freeCount, total: lessonCount })
                              : t('{{completed}} / {{total}} Lessons', { completed: completedCount, total: lessonCount })}
                          </p>
                          {lessonCount > 0 && !isPreviewMode && (
                            <div className="mt-1.5 bg-surface-100 rounded-full h-1.5 w-24">
                              <div
                                className="bg-primary-600 rounded-full h-1.5 transition-all"
                                style={{ width: `${(completedCount / lessonCount) * 100}%` }}
                              />
                            </div>
                          )}
                        </div>
                        {checkIsTeacher() && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/modules/${id}/categories/${category.id}/edit`);
                              }}
                              className="p-2 text-gray-400 hover:bg-surface-100 hover:text-primary-600 rounded-lg transition"
                              title={t('Edit Category')}
                              aria-label={t('Edit Category')}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCategory(category);
                              }}
                              className="p-2 text-red-500 hover:bg-red-50 hover:text-red-700 rounded-lg transition"
                              title={t('Delete')}
                              aria-label={t('Delete')}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="p-6 space-y-4 border-t border-surface-100">
                      {category.lessons && category.lessons.length > 0 ? (
                        category.lessons.map((lesson, index) => {
                          const progress = lessonProgress[lesson.id];
                          const isCompleted = progress?.is_completed || false;
                          const isStarted = !!progress;
                          const isLocked = isLessonLocked(lesson);

                          return (
                            <div
                              key={lesson.id}
                              className={`border border-surface-200 rounded-xl p-4 transition ${isLocked ? 'bg-surface-50 opacity-75' : 'hover:shadow-card hover:border-surface-300'}`}
                            >
                              <div className="flex items-start">
                                {lesson.thumbnail ? (
                                  <img
                                    src={lesson.thumbnail}
                                    alt=""
                                    className="w-16 h-16 rounded-xl object-cover shrink-0"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center shrink-0 text-primary-300">
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                  </div>
                                )}

                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ml-3 ${
                                  isLocked ? 'bg-gray-200 text-gray-400' :
                                  isCompleted ? 'bg-green-100 text-green-600' :
                                  isStarted ? 'bg-yellow-100 text-yellow-600' :
                                  'bg-primary-100 text-primary-600'
                                }`}>
                                  {isLocked ? (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                  ) : isCompleted ? '✓' : (lesson.order || index + 1)}
                                </div>

                                <div className="ml-4 flex-1">
                                  <div className="flex items-center justify-between">
                                    <h3 className={`text-lg font-bold ${isLocked ? 'text-gray-500' : 'text-gray-800'}`}>{lesson.title}</h3>
                                    {lesson.is_free && (
                                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                                        {t('FREE')}
                                      </span>
                                    )}
                                    {checkIsTeacher() && !lesson.is_published && (
                                      <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full font-medium">
                                        {t('Draft')}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {isLocked ? (
                                    <p className="text-gray-400 text-sm mt-1 italic">
                                      {isPreviewMode
                                        ? t('Enroll to unlock this lesson.')
                                        : t('Complete the previous lesson to unlock this one.')}
                                    </p>
                                  ) : lesson.content && (
                                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                                      {stripFormatting(lesson.content).substring(0, 150)}...
                                    </p>
                                  )}
                                  
                                  <div className="flex items-center mt-3 space-x-4">
                                    {lesson.duration_minutes && (
                                      <span className="text-xs text-gray-500 flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {t('{{count}} min', { count: lesson.duration_minutes })}
                                      </span>
                                    )}

                                    {lesson.video_url && (
                                      <span className="text-xs text-gray-500 flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        {t('Video')}
                                      </span>
                                    )}

                                    {isCompleted && (
                                      <span className="text-xs text-green-600 font-medium flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        {t('Completed')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="ml-4 flex items-center gap-2">
                                  {isLocked ? (
                                    <span className="text-xs text-gray-400 font-medium flex items-center gap-1">
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                      </svg>
                                      {t('Locked')}
                                    </span>
                                  ) : (
                                    <>
                                      {!isCompleted && (
                                        <IconButton
                                          color="primary"
                                          label={isPreviewMode ? t('Preview') : (isStarted ? t('Continue') : t('Start'))}
                                          onClick={() => navigate(`/modules/${id}/lessons/${lesson.id}${isPreviewMode ? '?preview=1' : ''}`)}
                                          icon={
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                          }
                                        />
                                      )}
                                      {!isPreviewMode && isStarted && !isCompleted && (
                                        <IconButton
                                          color="green"
                                          label={t('Mark Complete')}
                                          onClick={() => handleCompleteLesson(lesson.id)}
                                          icon={
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                          }
                                        />
                                      )}
                                      {isCompleted && (
                                        <IconButton
                                          color="gray"
                                          label={t('Review')}
                                          onClick={() => navigate(`/modules/${id}/lessons/${lesson.id}${isPreviewMode ? '?preview=1' : ''}`)}
                                          icon={
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                          }
                                        />
                                      )}
                                    </>
                                  )}
                                  {checkIsTeacher() && (
                                    <>
                                      {!lesson.is_published && (
                                        <IconButton
                                          color="green"
                                          label={publishingLessonId === lesson.id ? t('Publishing...') : t('Publish lesson')}
                                          onClick={() => {
                                            if (publishingLessonId !== lesson.id) handlePublishLesson(lesson.id);
                                          }}
                                          icon={
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                          }
                                        />
                                      )}
                                      <IconButton
                                        color="primary"
                                        label={t('Edit')}
                                        onClick={() => navigate(`/modules/${id}/lessons/${lesson.id}/edit`)}
                                        icon={
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                        }
                                      />
                                      {(isFeatureEnabled('resources') || isFeatureEnabled('quizzes')) && <IconButton
                                        color="purple"
                                        label={t('Materials/Quiz')}
                                        onClick={() => openMaterialsModal(lesson)}
                                        icon={
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                          </svg>
                                        }
                                      />}
                                      <IconButton
                                        color="red"
                                        label={t('Delete')}
                                        onClick={() => handleDeleteLesson(lesson.id)}
                                        icon={
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                        }
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <p>{t('No lessons in this category yet')}</p>
                          {checkIsTeacher() && (
                            <button
                              onClick={() => navigate(`/modules/${id}/lessons/create`)}
                              className="mt-3 text-primary-600 hover:text-primary-700 font-medium text-sm"
                            >
                              + {t('Add First Lesson')}
                            </button>
                          )}
                        </div>
                      )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-2xl shadow-card p-8 text-center">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('No Categories Yet')}</h3>
                  <p className="text-gray-600 mb-4">{t('Create categories to organize your lessons.')}</p>
                  {checkIsTeacher() && (
                    <button
                      onClick={() => navigate(`/modules/${id}/categories/create`)}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      + {t('Create First Category')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'materials' && isFeatureEnabled('resources') && (
            <div className="space-y-6">
              {/* All Materials Section */}
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <div className="px-6 py-4 border-b border-surface-100 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </span>
                  <div>
                    <h2 className="text-lg font-display font-bold text-gray-800">{t('Course Materials')}</h2>
                    <p className="text-gray-500 text-sm">{t('All attachments and resources')}</p>
                  </div>
                </div>

                <div className="p-6">
                  {allModuleMaterials.length > 0 ? (
                    <div className="space-y-4">
                      {allModuleMaterials.map((material) => (
                        <div key={material.id} className="border border-surface-200 rounded-xl p-4 hover:shadow-card hover:border-surface-300 transition">
                          <div className="flex items-start">
                            {isImageAttachment(material.file) ? (
                              <a
                                href={material.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-lg overflow-hidden mr-3 flex-shrink-0 block"
                                title={t('Open')}
                              >
                                <img src={material.file} alt={material.title} className="w-full h-full object-cover" />
                              </a>
                            ) : (
                              <a
                                href={material.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 bg-primary-100 hover:bg-primary-200 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 transition"
                                title={t('Open')}
                              >
                                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </a>
                            )}
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-800">{material.title}</h3>
                              <p className="text-sm text-gray-600 mt-1">{material.description}</p>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="text-xs text-primary-600 font-medium inline-flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                  </svg>
                                  {material.lessonTitle}
                                </span>
                                {material.file_size && (
                                  <span className="text-xs text-gray-500">
                                    {(material.file_size / 1024 / 1024).toFixed(2)} MB
                                  </span>
                                )}
                              </div>
                            </div>
                            <a
                              href={material.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium"
                            >
                              {t('Download')}
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p>{t('No materials uploaded yet')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Quizzes Tab */}
          {activeTab === 'quizzes' && isFeatureEnabled('quizzes') && (
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="px-6 py-4 border-b border-surface-100 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-lg font-display font-bold text-gray-800">{t('Course Quizzes')}</h2>
                  <p className="text-gray-500 text-sm">{t('All quizzes and assessments')}</p>
                </div>
              </div>

              <div className="p-6">
                {moduleQuizzesLoading && allModuleQuizzes.length === 0 ? (
                  <div className="flex items-center justify-center gap-3 py-10 text-gray-500" role="status">
                    <span className="w-5 h-5 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" />
                    <span>{t('Loading Quiz...')}</span>
                  </div>
                ) : allModuleQuizzes.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allModuleQuizzes.map((quiz) => (
                      <div key={quiz.id} className="border border-surface-200 rounded-xl p-4 hover:shadow-card hover:border-surface-300 transition">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-gray-800 flex-1">{quiz.title}</h3>
                          {checkIsTeacher() && (
                            <div className="flex items-center shrink-0">
                              <button
                                onClick={() => navigate(`/quizzes/${quiz.id}/edit`)}
                                className="ml-2 text-primary-500 hover:text-primary-700 transition p-1"
                                title={t('Edit Quiz')}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteQuiz(quiz.id)}
                                className="ml-1 text-red-500 hover:text-red-700 transition p-1"
                                title={t('Delete Quiz')}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                        {quiz.description && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{quiz.description}</p>
                        )}
                        <div className="space-y-2 mb-3">
                          <span className="text-xs text-purple-600 font-medium flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            {quiz.lessonTitle}
                          </span>
                          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                            {quiz.time_limit_minutes && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {t('{{count}} min', { count: quiz.time_limit_minutes })}
                              </span>
                            )}
                            {quiz.passing_score && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {t('{{score}}% to pass', { score: quiz.passing_score })}
                              </span>
                            )}
                            {quiz.question_count && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {t('{{count}} questions', { count: quiz.question_count })}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/quizzes/${quiz.id}`)}
                          className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium"
                        >
                          {t('Take Quiz')}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p>{t('No quizzes created yet')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Students Tab */}
          {activeTab === 'students' && (
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="px-6 py-4 border-b border-surface-100 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-lg font-display font-bold text-gray-800">{t('Enrolled Students')}</h2>
                  <p className="text-gray-500 text-sm">{t('{{count}} students enrolled', { count: enrolledStudentCount })}</p>
                </div>
              </div>

              <div className="p-6">
                {checkIsTeacher() && pendingEnrollmentCount > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h3 className="text-md font-semibold text-gray-800">{t('Pending Enrollment Requests')}</h3>
                      {selectedEnrollmentIds.length > 0 && (
                        <button
                          onClick={handleBulkApproveEnrollments}
                          disabled={bulkApproving}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition disabled:opacity-60"
                        >
                          {bulkApproving ? t('Approving...') : t('Approve {{count}} selected', { count: selectedEnrollmentIds.length })}
                        </button>
                      )}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 mt-3">
                      <input
                        type="checkbox"
                        checked={pendingEnrollments.length > 0 && selectedEnrollmentIds.length === pendingEnrollments.length}
                        onChange={() => setSelectedEnrollmentIds(
                          selectedEnrollmentIds.length === pendingEnrollments.length ? [] : pendingEnrollments.map(r => r.id)
                        )}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                      />
                      {t('Select all')}
                    </label>
                    <div className="space-y-3 mt-2">
                      {pendingEnrollments.map(req => (
                        <div
                          key={req.id}
                          id={`enrollment-request-${req.id}`}
                          onClick={() => setDetailEnrollment(req)}
                          className={`flex items-center justify-between bg-amber-50/60 border rounded-xl p-3 transition cursor-pointer hover:bg-amber-50 ${String(req.id) === String(focusedEnrollmentId) ? 'border-primary-400 ring-2 ring-primary-200' : 'border-amber-100'}`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedEnrollmentIds.includes(req.id)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSelectEnrollment(req.id)}
                              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded"
                            />
                            <div>
                              <p className="font-medium">{req.student?.first_name} {req.student?.last_name} <span className="text-xs text-gray-500">({req.student?.email})</span></p>
                              <p className="text-sm text-gray-600">{t('Requested')}: {new Date(req.created_at).toLocaleDateString()}</p>
                              {req.student_note && <p className="text-sm text-gray-500 mt-1">{t('Note')}: {req.student_note}</p>}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-primary-600">{t('View details')} →</span>
                        </div>
                      ))}
                    </div>
                    <Pagination
                      page={pendingStudentPage}
                      count={pendingEnrollmentCount}
                      pageSize={STUDENTS_PAGE_SIZE}
                      onPageChange={setPendingStudentPage}
                      className="mt-3 px-0"
                    />
                  </div>
                )}

                <div className="relative mb-4">
                  <svg className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="search"
                    value={studentSearch}
                    onChange={(event) => setStudentSearch(event.target.value)}
                    placeholder={t('Search students by name or email...')}
                    className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
                  />
                </div>

                {studentsLoading ? (
                  <div className="flex items-center justify-center gap-3 py-12 text-gray-500" role="status">
                    <span className="w-5 h-5 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin" />
                    <span>{t('Loading students...')}</span>
                  </div>
                ) : enrolledStudents.length > 0 ? (
                  <div className="space-y-3">
                    {enrolledStudents.map((enrollment) => (
                      <div key={enrollment.id} id={`enrollment-request-${enrollment.id}`} className={`border rounded-xl p-4 hover:shadow-card hover:border-surface-300 transition ${String(enrollment.id) === String(focusedEnrollmentId) ? 'border-primary-400 ring-2 ring-primary-200' : 'border-surface-200'}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                              {enrollment.student?.first_name?.[0]}{enrollment.student?.last_name?.[0]}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-800">
                                {enrollment.student?.first_name} {enrollment.student?.last_name}
                              </h3>
                              <p className="text-sm text-gray-600">{enrollment.student?.email}</p>
                              <span className="text-xs text-gray-500">
                                {t('Enrolled')}: {new Date(enrollment.created_at || enrollment.start_date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isFeatureEnabled('certificates') && enrollment.certificate_status === 'pending' && (
                              <button
                                onClick={() => handleApproveCertificate(enrollment.id)}
                                disabled={approvingCertificateId === enrollment.id}
                                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium disabled:opacity-60"
                              >
                                {approvingCertificateId === enrollment.id ? t('Approving...') : t('Approve Certificate')}
                              </button>
                            )}
                            {isFeatureEnabled('certificates') && enrollment.certificate_status === 'approved' && (
                              <span className="px-2.5 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-semibold">{t('Certificate approved')}</span>
                            )}
                            <button
                              onClick={() => {
                                setSelectedStudent(enrollment.student);
                                fetchStudentProgress(enrollment.student.id);
                                setShowProgressModal(true);
                              }}
                              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition"
                              title={t('View Progress')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedStudent(enrollment.student);
                                fetchStudentQuizHistory(enrollment.student.id);
                                setShowQuizHistoryModal(true);
                              }}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                              title={t('Quiz History')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedStudent(enrollment.student);
                                setShowMessageModal(true);
                              }}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                              title={t('Send Message')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <Pagination
                      page={studentPage}
                      count={enrolledStudentCount}
                      pageSize={STUDENTS_PAGE_SIZE}
                      onPageChange={setStudentPage}
                      className="mt-3 px-0"
                    />
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <p>{debouncedStudentSearch ? t('No students match your search') : t('No students enrolled yet')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && isFeatureEnabled('messaging') && (
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="px-6 py-4 border-b border-surface-100 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-lg font-display font-bold text-gray-800">{t('Group Messages')}</h2>
                  <p className="text-gray-500 text-sm">{t('Communication hub for this module')}</p>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2 flex flex-col h-96 border border-surface-200 rounded-xl overflow-hidden">
                      <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                            {module?.thumbnail ? (
                              <img src={module.thumbnail} alt={module?.name} className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold">{groupChat ? groupChat.name : t('Module group chat')}</h3>
                            <p className="text-sm text-gray-500">{module ? `${module.name}` : t('Group chat')}</p>
                          </div>
                        </div>
                      </div>
                    <div className="flex-1 p-4 overflow-y-auto bg-surface-50" id="group-chat-list">
                      {groupMessages.length === 0 ? (
                        <div className="text-center text-gray-400 py-8">{t('No messages yet — be the first to say hello.')}</div>
                      ) : (
                        groupMessages.map((msg, idx) => {
                          const isOwn = msg.sender && String(msg.sender.id) === String(currentUserId);
                          const prev = groupMessages[idx - 1];
                          const showAvatar = !isOwn && (!prev || String(prev.sender?.id) !== String(msg.sender?.id));
                          const avatarSrc = msg.sender?.profile_picture || (`https://ui-avatars.com/api/?name=${encodeURIComponent((msg.sender?.first_name || '') + ' ' + (msg.sender?.last_name || ''))}&background=ddd&color=555`);
                          return (
                            <div key={msg.id} className={`mb-3 flex items-end ${isOwn ? 'justify-end' : 'justify-start'}`}>
                              {!isOwn && showAvatar && (
                                <div className="flex-shrink-0 mr-3">
                                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                                    <img src={avatarSrc} alt={`${msg.sender?.first_name || ''} ${msg.sender?.last_name || ''}`} className="w-full h-full object-cover" />
                                  </div>
                                </div>
                              )}

                              <div className={`max-w-[70%] ${isOwn ? 'text-right' : 'text-left'}`}>
                                {!isOwn && (
                                  <div className="text-sm text-gray-600 font-medium">{msg.sender?.first_name} {msg.sender?.last_name}</div>
                                )}
                                <div className={`${isOwn ? 'bg-primary-600 text-white' : 'bg-white text-gray-800'} rounded-lg p-3 shadow-sm mt-1`}>
                                  {msg.content && <div>{msg.content}</div>}
                                  {msg.attachment && (
                                    isImageAttachment(msg.attachment) ? (
                                      <a href={msg.attachment} target="_blank" rel="noopener noreferrer" className="block mt-1">
                                        <img
                                          src={msg.attachment}
                                          alt={t('Image attachment')}
                                          className="max-w-[220px] max-h-[220px] rounded-lg object-cover border border-black/10"
                                        />
                                      </a>
                                    ) : (
                                      <a
                                        href={msg.attachment}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 mt-1 px-3 py-2 rounded-lg border text-sm ${isOwn ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                                      >
                                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="truncate max-w-[180px]">{attachmentFileName(msg.attachment) || t('Attachment')}</span>
                                      </a>
                                    )
                                  )}
                                  {isOwn && msg.is_read && (
                                    <span className="ml-2 text-xs text-gray-200">✓</span>
                                  )}
                                </div>
                                <div className="text-xs mt-1 text-gray-400">{new Date(msg.created_at).toLocaleString()}</div>
                              </div>

                              {isOwn && (
                                <div className="flex-shrink-0 ml-3">
                                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
                                    <img src={user?.profile_picture || (`https://ui-avatars.com/api/?name=${encodeURIComponent((user?.first_name || '') + ' ' + (user?.last_name || ''))}&background=2b6cb0&color=fff`)} alt="You" className="w-full h-full object-cover" />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    <div className="p-4 border-t bg-white">
                      <div className="flex gap-2">
                        <textarea value={newGroupMessage} onChange={e => setNewGroupMessage(e.target.value)} rows={3} className="flex-1 px-3 py-2 border rounded" placeholder={t('Write a message to the group...')} />
                        <input type="file" ref={groupAttachmentInputRef} onChange={handleGroupAttachmentChange} className="hidden" />
                        <div className="flex flex-col gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => groupAttachmentInputRef.current?.click()}
                            title={t('Attach a file')}
                            aria-label={t('Attach a file')}
                            className="p-2 text-gray-400 hover:text-gray-600 border rounded"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                          </button>
                          <button onClick={handleSendGroupMessage} className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700">{t('Send')}</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-1">
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-3">{t('Private Messages')}</h4>
                      <div className="space-y-2">
                          <button
                            type="button"
                            id="message-teacher-btn"
                            aria-label={t('Message Teacher')}
                            onClick={async () => {
                              const resolveTeacherId = (m) => {
                                if (!m) return null;
                                const pick = (v) => {
                                  if (v === undefined || v === null) return null;
                                  if (typeof v === 'object') return v.id || null;
                                  return v;
                                };
                                if (Array.isArray(m.teachers) && m.teachers.length) return pick(m.teachers[0]);
                                if (Array.isArray(m.instructors) && m.instructors.length) return pick(m.instructors[0]);
                                const candidates = [m.created_by, m.created_by_id, m.owner, m.owner_id, m.instructor, m.instructor_id, m.teacher, m.teacher_id];
                                for (const c of candidates) {
                                  const p = pick(c);
                                  if (p) return p;
                                }
                                return null;
                              };

                              try {
                                let teacherId = resolveTeacherId(module);
                                if (!teacherId && module?.id) {
                                  try {
                                    const resp = await academicService.getModule(module.id);
                                    teacherId = resolveTeacherId(resp.data || resp);
                                  } catch (fetchErr) {
                                    // fall through; handled by the guard below
                                  }
                                }

                                if (!teacherId || !user) return;
                                navigate(`/messages?target_user=${teacherId}`, { state: { openConversation: { type: 'private', userId: teacherId } } });
                              } catch (err) {
                                addNotification(t('Failed to open conversation'), 'error');
                              }
                            }}
                            className="w-full text-left px-3 py-2 bg-gray-100 rounded hover:bg-gray-200"
                          >
                            {t('Message Teacher')}
                          </button>
                        {checkIsTeacher() && (
                          <div>
                            <p className="text-sm text-gray-500 mt-3 mb-2">{t('Message a student')}</p>
                            <select onChange={e => {
                              const sid = e.target.value;
                              const stud = enrolledStudents.find(en => String(en.student?.id) === String(sid));
                              if (stud) { setSelectedStudent(stud.student); setShowMessageModal(true); }
                            }} className="w-full px-3 py-2 border rounded">
                              <option value="">{t('Select student...')}</option>
                              {enrolledStudents.map(en => (
                                <option key={en.id} value={en.student.id}>{en.student.first_name} {en.student.last_name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && isFeatureEnabled('calendar') && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <div className="px-6 py-4 border-b border-surface-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <div>
                      <h2 className="text-lg font-display font-bold text-gray-800">{t('Module Events')}</h2>
                      <p className="text-gray-500 text-sm">{t('Schedule meetings and sessions')}</p>
                    </div>
                  </div>
                  {checkIsTeacher() && (
                    <button
                      onClick={() => setShowEventModal(true)}
                      className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition font-medium text-sm shrink-0"
                    >
                      + {t('Event')}
                    </button>
                  )}
                </div>
                
                <div className="p-6">
                  {moduleEvents.length > 0 ? (
                    <div className="space-y-4">
                      {moduleEvents.map((event) => (
                        <div key={event.id} className="bg-white border border-surface-200 rounded-xl p-4 hover:shadow-card hover:border-surface-300 transition">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <span className="shrink-0 w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </span>
                              <div className="min-w-0 flex-1">
                                <h3 className="text-base font-display font-bold text-gray-800 mb-1">{event.title}</h3>
                                {event.description && (
                                  <p className="text-gray-600 text-sm mb-3">{event.description}</p>
                                )}
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-2 text-sm text-gray-700">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="font-medium">
                                      {new Date(event.event_datetime).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-700">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                      {new Date(event.event_datetime).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })} {t('({{count}} minutes)', { count: event.duration_minutes })}
                                    </span>
                                  </div>
                                  {event.meeting_link && (
                                    <div className="flex items-center gap-2">
                                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                      <a
                                        href={event.meeting_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:text-primary-700 text-sm font-medium underline"
                                      >
                                        {t('Join Meeting')}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                new Date(event.event_datetime) > new Date()
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-surface-100 text-gray-600'
                              }`}>
                                {new Date(event.event_datetime) > new Date() ? t('Upcoming') : t('Past')}
                              </span>
                              {checkIsTeacher() && isFeatureEnabled('attendance') && (
                                <button
                                  onClick={() => setAttendanceEvent(event)}
                                  className="text-xs font-medium text-primary-600 hover:underline whitespace-nowrap"
                                >
                                  {t('Take Attendance')}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="mb-4">{t('No events scheduled yet')}</p>
                      {checkIsTeacher() && (
                        <button
                          onClick={() => setShowEventModal(true)}
                          className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
                        >
                          {t('Create First Event')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && isFeatureEnabled('ratings') && (
            <ModuleReviews moduleId={module.id} canReview={!checkIsTeacher() && isUserEnrolled} />
          )}
        </main>
      </div>

      {/* Create Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="p-5 border-b border-surface-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                <h2 className="text-lg font-display font-bold text-gray-800">{t('Create Event')}</h2>
              </div>
              <button
                onClick={() => setShowEventModal(false)}
                className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateEvent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Event Title')} *</label>
                <input
                  type="text"
                  name="title"
                  value={eventForm.title}
                  onChange={handleEventFormChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder={t('e.g., Live Q&A Session')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Description')}</label>
                <textarea
                  name="description"
                  value={eventForm.description}
                  onChange={handleEventFormChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder={t('Describe what this event is about...')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Date')} *</label>
                  <input
                    type="date"
                    name="event_date"
                    value={eventForm.event_date}
                    onChange={handleEventFormChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Time')} *</label>
                  <input
                    type="time"
                    name="event_time"
                    value={eventForm.event_time}
                    onChange={handleEventFormChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Duration (minutes)')}</label>
                <input
                  type="number"
                  name="duration_minutes"
                  value={eventForm.duration_minutes}
                  onChange={handleEventFormChange}
                  min="15"
                  step="15"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Meeting Link')}</label>
                <input
                  type="url"
                  name="meeting_link"
                  value={eventForm.meeting_link}
                  onChange={handleEventFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">{t('Zoom, Google Meet, Teams, or any meeting platform link')}</p>
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-primary-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-primary-800">
                    <p className="font-medium mb-1">{t('All enrolled students will be notified')}</p>
                    <p className="text-primary-600">{t('This event will be added to their calendars automatically')}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEventModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  {t('Cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
                >
                  {t('Create Event')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Materials/Quiz Modal */}
      {showMaterialsModal && selectedLesson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-surface-100 flex items-center justify-between">
              <h2 className="text-lg font-display font-bold text-gray-800">
                {t('Manage')}: {selectedLesson.title}
              </h2>
              <button
                onClick={() => setShowMaterialsModal(false)}
                className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-2 border-b border-surface-100">
              {isFeatureEnabled('resources') && <button
                onClick={() => setMaterialsTab('materials')}
                className={`px-4 py-2 rounded-xl font-medium transition text-sm ${
                  materialsTab === 'materials'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-surface-100'
                }`}
              >
                {t('Materials')}
              </button>}
              {isFeatureEnabled('quizzes') && <button
                onClick={() => setMaterialsTab('quiz')}
                className={`px-4 py-2 rounded-xl font-medium transition text-sm ${
                  materialsTab === 'quiz'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-surface-100'
                }`}
              >
                {t('Quiz')}
              </button>}
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Materials Tab */}
              {materialsTab === 'materials' && isFeatureEnabled('resources') && (
                <div className="space-y-6">
                  {/* Upload Form */}
                  <form onSubmit={handleUploadMaterial} className="bg-surface-50 p-4 rounded-xl space-y-4">
                    <h3 className="font-semibold text-gray-800">{t('Upload New Material')}</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('Title')} *</label>
                      <input
                        type="text"
                        name="title"
                        value={materialForm.title}
                        onChange={handleMaterialFormChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('Description')}</label>
                      <textarea
                        name="description"
                        value={materialForm.description}
                        onChange={handleMaterialFormChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('File')} *</label>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={uploadingFile}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                    >
                      {uploadingFile ? t('Uploading...') : t('Upload Material')}
                    </button>
                  </form>

                  {/* Materials List */}
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">{t('Uploaded Materials')}</h3>
                    {attachments.length > 0 ? (
                      <div className="space-y-2">
                        {attachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center gap-3 p-3 border border-surface-200 rounded-xl">
                            {isImageAttachment(attachment.file) ? (
                              <a href={attachment.file} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg overflow-hidden shrink-0 block" title={t('Open')}>
                                <img src={attachment.file} alt={attachment.title} className="w-full h-full object-cover" />
                              </a>
                            ) : (
                              <a href={attachment.file} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-primary-50 hover:bg-primary-100 rounded-lg flex items-center justify-center shrink-0 transition" title={t('Open')}>
                                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </a>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-800 truncate">{attachment.title}</h4>
                              {attachment.description && (
                                <p className="text-sm text-gray-600 truncate">{attachment.description}</p>
                              )}
                              {attachment.file_size && (
                                <p className="text-xs text-gray-400">
                                  {(attachment.file_size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <a
                                href={attachment.file}
                                download
                                className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition font-medium"
                              >
                                {t('Download')}
                              </a>
                              {checkIsTeacher() && (
                                <button
                                  onClick={() => handleDeleteMaterial(attachment.id)}
                                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition font-medium"
                                  title={t('Delete material')}
                                >
                                  {t('Delete')}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">{t('No materials uploaded yet')}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Quiz Tab */}
              {materialsTab === 'quiz' && isFeatureEnabled('quizzes') && (
                <div className="space-y-6">
                  {/* Existing Quizzes */}
                  {quizzes.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3">{t('Existing Quizzes')}</h3>
                      <div className="space-y-2 mb-6">
                        {quizzes.map((quiz) => (
                          <div key={quiz.id} className="p-3 border rounded-lg">
                            <div className="flex justify-between items-start gap-2">
                              <h4 className="font-medium text-gray-800 flex-1">{quiz.title}</h4>
                              <div className="flex items-center shrink-0">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/quizzes/${quiz.id}/edit`)}
                                  className="text-primary-500 hover:text-primary-700 transition p-1"
                                  title={t('Edit Quiz')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteQuiz(quiz.id)}
                                  className="text-red-500 hover:text-red-700 transition p-1"
                                  title={t('Delete Quiz')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            {quiz.description && <p className="text-sm text-gray-600">{quiz.description}</p>}
                            <div className="flex items-center mt-2 gap-3 text-xs text-gray-500">
                              {quiz.time_limit_minutes && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {t('{{count}} min', { count: quiz.time_limit_minutes })}
                                </span>
                              )}
                              {quiz.passing_score && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {quiz.passing_score}%
                                </span>
                              )}
                              {quiz.randomize_questions && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  {t('Random Order')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Create Quiz Form */}
                  <form onSubmit={handleCreateQuiz} className="space-y-4">
                    <h3 className="font-semibold text-gray-800">{t('Create New Quiz')}</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Quiz Title')} *</label>
                        <input
                          type="text"
                          name="title"
                          value={quizForm.title}
                          onChange={handleQuizFormChange}
                          className="w-full px-3 py-2 border rounded-lg"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Time Limit (minutes)')}</label>
                        <input
                          type="number"
                          name="time_limit_minutes"
                          value={quizForm.time_limit_minutes}
                          onChange={handleQuizFormChange}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('Description')}</label>
                      <textarea
                        name="description"
                        value={quizForm.description}
                        onChange={handleQuizFormChange}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Passing Score (%)')}</label>
                        <input
                          type="number"
                          name="passing_score"
                          value={quizForm.passing_score}
                          onChange={handleQuizFormChange}
                          className="w-full px-3 py-2 border rounded-lg"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="randomize_questions"
                            checked={quizForm.randomize_questions}
                            onChange={handleQuizFormChange}
                            className="w-5 h-5 text-primary-600 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">{t('Randomize Question Order')}</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="show_correct_answers"
                          checked={quizForm.show_correct_answers}
                          onChange={handleQuizFormChange}
                          className="w-5 h-5 text-primary-600 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">{t('Show results to students after submission')}</span>
                      </label>
                      <InfoTooltip text={t('If unchecked, students will only see their score and pass/fail status — not which questions they got right or wrong.')} />
                    </div>

                    {/* Bulk import from Excel */}
                    <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-lg space-y-2">
                      <h4 className="font-semibold text-gray-800">{t('Bulk Import Questions (Excel)')}</h4>
                      <p className="text-sm text-gray-600">
                        {t('Upload a spreadsheet of questions and options instead of adding them one by one.')}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <button
                          type="button"
                          onClick={downloadExcelTemplate}
                          className="text-sm font-medium text-primary-600 hover:text-primary-700 underline"
                        >
                          {t('Download Excel Template')}
                        </button>
                        <input
                          type="file"
                          ref={excelInputRef}
                          accept=".xlsx,.xls,.csv"
                          onChange={handleExcelImport}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => excelInputRef.current?.click()}
                          className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
                        >
                          {t('Import from Excel')}
                        </button>
                        <InfoTooltip text={t('Excel format hint: one row = one question. Columns: Question (required), Option 1-6 (at least 2 required, leave extra columns blank), Correct Answer (the option number, e.g. "2", or comma-separated for multiple correct answers, e.g. "1,3"), Points (optional, default 1), and Explanation (optional, shown to students after they answer).')} />
                      </div>
                    </div>

                    {/* Question Builder */}
                    <div className="bg-surface-50 p-4 rounded-xl space-y-4">
                      <h4 className="font-semibold text-gray-800">{t('Add Question')}</h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Question Text')} *</label>
                        <textarea
                          name="question_text"
                          value={questionForm.question_text}
                          onChange={handleQuestionFormChange}
                          className="w-full px-3 py-2 border rounded-lg"
                          rows={2}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Question Image (optional)')}</label>
                        {questionForm.imagePreview ? (
                          <div className="flex items-center gap-3">
                            <img src={questionForm.imagePreview} alt="" className="h-20 w-20 object-cover rounded-lg border" />
                            <button
                              type="button"
                              onClick={removeQuestionImage}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              {t('Remove')}
                            </button>
                          </div>
                        ) : (
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleQuestionImageChange}
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
                          />
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Points')}</label>
                          <input
                            type="number"
                            name="points"
                            value={questionForm.points}
                            onChange={handleQuestionFormChange}
                            className="w-full px-3 py-2 border rounded-lg"
                            min="1"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Order')}</label>
                          <input
                            type="number"
                            name="order"
                            value={questionForm.order}
                            onChange={handleQuestionFormChange}
                            className="w-full px-3 py-2 border rounded-lg"
                            min="0"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('Answer Options')}</label>
                        {questionForm.options.map((option, index) => (
                          <div key={index} className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              value={option.option_text}
                              onChange={(e) => handleOptionChange(index, 'option_text', e.target.value)}
                              placeholder={t('Option {{number}}', { number: index + 1 })}
                              className="flex-1 px-3 py-2 border rounded-lg"
                            />
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={option.is_correct}
                                onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                                className="w-4 h-4 text-green-600"
                              />
                              <span className="ml-1 text-xs text-gray-600">{t('Correct')}</span>
                            </label>
                            {questionForm.options.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeOption(index)}
                                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addOption}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          + {t('Add Option')}
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={addQuestionToQuiz}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        {t('Add Question to Quiz')}
                      </button>
                    </div>

                    {/* Added Questions */}
                    {quizForm.questions.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">{t('Added Questions ({{count}})', { count: quizForm.questions.length })}</h4>
                        <div className="space-y-2">
                          {quizForm.questions.map((q, idx) => (
                            <div key={idx} className="p-3 bg-primary-50 rounded-lg flex gap-3">
                              {q.imagePreview && (
                                <img src={q.imagePreview} alt="" className="h-14 w-14 object-cover rounded-lg border shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="font-medium text-gray-800">{idx + 1}. {q.question_text}</p>
                                  <button
                                    type="button"
                                    onClick={() => setQuizForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }))}
                                    className="text-xs text-gray-400 hover:text-red-500 shrink-0"
                                  >
                                    {t('Remove')}
                                  </button>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {t('{{count}} point(s) | Order: {{order}}', { count: q.points, order: q.order })}
                                  {q.allow_multiple_answers && ` • ${t('Multiple answers allowed')}`}
                                </p>
                                <div className="text-xs text-gray-500 mt-1">
                                  {q.options.map((opt, i) => (
                                    <div key={i} className={opt.is_correct ? 'text-green-600 font-medium' : ''}>
                                      • {opt.option_text} {opt.is_correct && '✓'}
                                    </div>
                                  ))}
                                </div>
                                {q.explanation && (
                                  <p className="text-xs text-gray-500 mt-1 italic">{t('Explanation')}: {q.explanation}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={
                        quizForm.questions.length === 0 &&
                        !(questionForm.question_text.trim() && questionForm.options.filter(o => o.option_text.trim()).length >= 2)
                      }
                      className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {t('Create Quiz')}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enroll Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="p-5 border-b border-surface-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </span>
                <h2 className="text-lg font-display font-bold text-gray-800">{t('Request Enrollment')}</h2>
              </div>
              <button
                onClick={() => setShowEnrollModal(false)}
                className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={submitEnrollment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Grade')} *</label>
                <select name="grade" value={enrollForm.grade} onChange={handleEnrollChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  {module.grades && module.grades.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Subscription')} *</label>
                <select name="subscription" value={enrollForm.subscription} onChange={handleEnrollChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">{t('Select a plan')}</option>
                  {subscriptions.map(s => (
                    <option key={s.id} value={s.id}>{s.name} — ${s.price}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Note (optional)')}</label>
                <textarea name="student_note" value={enrollForm.student_note} onChange={handleEnrollChange} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={t('Any notes for the teacher or admin')} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Attachment (proof) — optional')}</label>
                <input type="file" name="attachment" accept="image/jpeg,image/png,application/pdf" onChange={handleEnrollChange} />
                <p className="text-xs text-gray-500 mt-1">{t('Upload payment proof or other supporting document (jpg, png, pdf)')}</p>
                {enrollErrors.attachment && <p className="text-sm text-red-600 mt-2">{enrollErrors.attachment}</p>}
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowEnrollModal(false)} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">{t('Cancel')}</button>
                <button type="submit" disabled={!!enrollErrors.attachment} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50">{t('Submit Request')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Student Progress Modal */}
      {showProgressModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="p-5 border-b border-surface-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-lg font-display font-bold text-gray-800">{t('Student Progress')}</h2>
                  <p className="text-gray-500 text-sm">
                    {selectedStudent.first_name} {selectedStudent.last_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowProgressModal(false)}
                className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {studentProgress.length > 0 ? (
                <div className="space-y-3">
                  {studentProgress.map((progress) => (
                    <div key={progress.id} className="border border-surface-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-800">{t('Lesson #{{id}}', { id: progress.lesson })}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {t('Started')}: {new Date(progress.started_at).toLocaleDateString()}
                          </p>
                          {progress.completed_at && (
                            <p className="text-sm text-green-600 mt-1">
                              {t('Completed')}: {new Date(progress.completed_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div>
                          {progress.is_completed ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              ✓ {t('Completed')}
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                              {t('In Progress')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('No progress recorded yet')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Student Quiz History Modal */}
      {showQuizHistoryModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="p-5 border-b border-surface-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-lg font-display font-bold text-gray-800">{t('Quiz History')}</h2>
                  <p className="text-gray-500 text-sm">
                    {selectedStudent.first_name} {selectedStudent.last_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowQuizHistoryModal(false)}
                className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {studentQuizHistory.length > 0 ? (
                <div className="space-y-3">
                  {studentQuizHistory.map((attempt) => (
                    <button
                      type="button"
                      key={attempt.id}
                      onClick={() => openAttemptDetail(attempt)}
                      className="w-full text-left border border-surface-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-card transition"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{attempt.quiz_title || t('Quiz #{{id}}', { id: attempt.quiz })}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {t('Attempted')}: {new Date(attempt.started_at).toLocaleString()}
                          </p>
                          {attempt.submitted_at && (
                            <p className="text-sm text-gray-600">
                              {t('Completed')}: {new Date(attempt.submitted_at).toLocaleString()}
                            </p>
                          )}
                          <p className="text-xs text-primary-600 font-medium mt-1">{t('Click to see details')} →</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-800">
                            {attempt.points_earned || 0}/{attempt.total_points || 0}
                          </div>
                          <div className={`text-sm font-medium mt-1 ${
                            attempt.passed ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {attempt.passed ? `✓ ${t('Passed')}` : `✗ ${t('Failed')}`}
                          </div>
                          {attempt.time_spent_seconds && (
                            <div className="text-xs text-gray-500 mt-1">
                              {t('Time')}: {Math.floor(attempt.time_spent_seconds / 60)}m {attempt.time_spent_seconds % 60}s
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('No quiz attempts yet')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Attempt Detail Modal — per-question breakdown of a single quiz attempt */}
      {attemptDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="p-5 border-b border-surface-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h2 className="text-lg font-display font-bold text-gray-800 truncate">{attemptDetail.quiz_title || t('Attempt Details')}</h2>
                  <p className="text-gray-500 text-sm truncate">
                    {attemptDetail.student_name} • {t('Score')}: {attemptDetail.score}% • {attemptDetail.points_earned}/{attemptDetail.total_points} {t('points')}
                  </p>
                </div>
              </div>
              <button
                onClick={closeAttemptDetail}
                className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {loadingAttemptDetail ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : attemptDetailQuiz ? (
                <div className="space-y-5">
                  {(attemptDetailQuiz.questions || []).map((question, index) => {
                    const response = (attemptDetail.responses || []).find((r) => r.question === question.id);
                    const selectedIds = response?.selected_answers || [];
                    return (
                      <div key={question.id} className="border border-surface-200 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-medium text-gray-800 flex-1">{index + 1}. {question.text}</p>
                          {response && (
                            <span className={`text-xs font-medium px-2 py-1 rounded-full shrink-0 ${response.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {response.is_correct ? t('Correct') : t('Incorrect')} · {response.points_earned || 0}/{question.points} {t('pts')}
                            </span>
                          )}
                        </div>
                        <ul className="mt-3 space-y-1.5">
                          {(question.answers || []).map((answer) => {
                            const wasSelected = selectedIds.includes(answer.id);
                            const isCorrectAnswer = answer.is_correct;
                            return (
                              <li
                                key={answer.id}
                                className={`text-sm flex items-center gap-2 px-3 py-2 rounded-lg border ${
                                  isCorrectAnswer ? 'border-green-300 bg-green-50' : wasSelected ? 'border-red-300 bg-red-50' : 'border-gray-100'
                                }`}
                              >
                                {isCorrectAnswer ? (
                                  <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                ) : wasSelected ? (
                                  <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                ) : (
                                  <span className="w-4 h-4 shrink-0" />
                                )}
                                <span className={isCorrectAnswer ? 'text-green-800 font-medium' : wasSelected ? 'text-red-800' : 'text-gray-600'}>
                                  {answer.text}
                                </span>
                                {wasSelected && <span className="text-xs text-gray-500 ml-auto">{t('Student\'s answer')}</span>}
                              </li>
                            );
                          })}
                        </ul>
                        {question.explanation && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600"><span className="font-semibold">{t('Note:')}</span> {question.explanation}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">{t('Failed to load attempt details')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <CourseStatsModal
        open={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        moduleId={id}
        moduleName={module?.name}
      />

      <PedagogicalNoteModal
        open={showPedagogicalNoteModal}
        onClose={() => setShowPedagogicalNoteModal(false)}
        module={module}
        currentUser={user}
      />

      {/* Send Message Modal */}
      {showMessageModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="p-5 border-b border-surface-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-lg font-display font-bold text-gray-800">{t('Send Message')}</h2>
                  <p className="text-gray-500 text-sm">
                    {t('To')}: {selectedStudent.first_name} {selectedStudent.last_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!selectedStudent) return;
              try {
                // find existing conversation
                const res = await messagingService.getPrivateConversations();
                const data = res.data || res;
                const convos = Array.isArray(data) ? data : data.results || [];
                let convo = convos.find(c => (c.participant1 === selectedStudent.id || c.participant2 === selectedStudent.id));
                if (!convo) {
                  // create conversation by sending target user id (backend will pair with current user)
                  const createRes = await messagingService.createPrivateConversation({ user_id: selectedStudent.id });
                  convo = createRes.data;
                }

                // send message
                await messagingService.sendPrivateMessage(convo.id, { content: privateMessageText });
                addNotification(t('Message sent'), 'success');
                setPrivateMessageText('');
                setPrivateMessageSubject('');
                setShowMessageModal(false);
              } catch (err) {
                addNotification(t('Failed to send message'), 'error');
              }
            }} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Subject')}</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder={t('Message subject...')}
                  value={privateMessageSubject}
                  onChange={e => setPrivateMessageSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Message')}</label>
                <textarea
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder={t('Type your message here...')}
                  value={privateMessageText}
                  onChange={e => setPrivateMessageText(e.target.value)}
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowMessageModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  {t('Cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                >
                  {t('Send Message')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isFeatureEnabled('attendance') && <EventAttendanceModal event={attendanceEvent} onClose={() => setAttendanceEvent(null)} />}

      <PendingEnrollmentModal
        enrollment={detailEnrollment}
        onClose={() => setDetailEnrollment(null)}
        onApproved={handleEnrollmentDecided}
        onRejected={handleEnrollmentDecided}
      />
    </div>
  );
}

export default ModuleDetail;
